<!-- src/views/OnBoarding/PpeRequestFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">PPE Request Form</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Personal Protective Equipment Procurement • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Official Request</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <!-- Logic Instruction Alert -->
          <div class="mb-10 p-6 bg-red-50 border-l-8 border-red-500 rounded-2xl flex items-center gap-4">
            <ExclamationTriangleIcon class="h-8 w-8 text-red-600 flex-shrink-0" />
            <p class="text-xs font-black uppercase tracking-widest text-red-900 leading-relaxed">
              Important: If you do not require any new equipment, please skip directly to Section 3.
            </p>
          </div>

          <!-- Dynamic Introductory Text -->
          <section 
            v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" 
            class="mb-12 p-8 bg-slate-50 rounded-3xl border border-slate-200 shadow-inner"
          >
            <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2">
               <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
               <span class="font-black uppercase text-xs">Loading Requisition Guidelines...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-700" v-html="mainTextContent"></div>
          </section>

          <form @submit.prevent="submitForm" class="space-y-16">
            
            <!-- Section 1: Personnel Verification -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Your Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">First Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.firstName || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Last Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.lastName || 'Loading...' }}</p>
                </div>
                <div class="md:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Verified Email</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
              </div>
            </section>

            <!-- Section 2: PPE Requisition -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <WrenchScrewdriverIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Equipment Requisition</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 p-8 bg-slate-50 rounded-[32px] border border-slate-100">
                <div v-for="item in ppeItems" :key="item.id" 
                  :class="item.selected ? 'bg-[#EBFDF8] border-[#20FCF1] shadow-md' : 'bg-white border-slate-200'"
                  class="p-6 border rounded-3xl transition-all duration-300 group"
                >
                  <div class="flex items-center justify-between">
                    <label :for="item.id" class="flex items-center cursor-pointer">
                      <input :id="item.id" type="checkbox" v-model="item.selected" class="h-6 w-6 text-[#0A1630] border-slate-300 rounded-lg focus:ring-[#20FCF1]">
                      <span class="ml-4 font-black uppercase text-xs tracking-widest text-slate-700 group-hover:text-[#0A1630] transition-colors">{{ item.name }}</span>
                    </label>
                  </div>
                  
                  <div v-if="item.selected" class="mt-6 grid grid-cols-2 gap-4 pt-6 border-t border-[#20FCF1]/20 animate-fadeIn">
                    <div>
                      <label :for="`${item.id}-size`" class="form-label text-[#20FCF1]">Size</label>
                      <input v-if="item.id === 'safetyFootwear'" type="number" :id="`${item.id}-size`" v-model="item.size" class="form-input" placeholder="UK Size">
                      <select v-else :id="`${item.id}-size`" v-model="item.size" class="form-input">
                        <option>S</option> <option>M</option> <option>L</option> <option>XL</option> <option>XXL</option>
                      </select>
                    </div>
                    <div>
                      <label :for="`${item.id}-qty`" class="form-label text-[#20FCF1]">Quantity</label>
                      <input type="number" :id="`${item.id}-qty`" v-model.number="item.qty" min="1" class="form-input">
                    </div>
                  </div>
                </div>

                <!-- Other Item Card -->
                <div 
                  :class="otherItem.selected ? 'bg-[#EBFDF8] border-[#20FCF1] shadow-md' : 'bg-white border-slate-200'"
                  class="p-6 border rounded-3xl transition-all duration-300 md:col-span-2"
                >
                    <label for="other" class="flex items-center cursor-pointer">
                      <input id="other" type="checkbox" v-model="otherItem.selected" class="h-6 w-6 text-[#0A1630] border-slate-300 rounded-lg focus:ring-[#20FCF1]">
                      <span class="ml-4 font-black uppercase text-xs tracking-widest text-slate-700">Other Equipment / Requests</span>
                    </label>
                    <div v-if="otherItem.selected" class="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-[#20FCF1]/20 animate-fadeIn">
                      <div>
                        <label for="other-name" class="form-label text-[#20FCF1]">Item Name</label>
                        <input type="text" id="other-name" v-model="otherItem.name" class="form-input" placeholder="Specify item...">
                      </div>
                      <div>
                        <label for="other-size" class="form-label text-[#20FCF1]">Size/Spec</label>
                        <input type="text" id="other-size" v-model="otherItem.size" class="form-input" placeholder="Size info">
                      </div>
                      <div>
                        <label for="other-qty" class="form-label text-[#20FCF1]">Qty</label>
                        <input type="number" id="other-qty" v-model.number="otherItem.qty" min="1" class="form-input">
                      </div>
                    </div>
                 </div>
              </div>
            </section>

            <!-- Section 3: Confirmation -->
            <section class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
                <div class="absolute top-0 right-0 p-10 opacity-5">
                  <ShieldCheckIcon class="h-32 w-32" />
                </div>
                <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div class="max-w-xl text-left">
                      <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-2">3. Declaration</h2>
                      <p class="text-slate-400 font-medium leading-relaxed">Please acknowledge your responsibility regarding the maintenance and usage of the equipment.</p>
                    </div>
                    
                    <div class="bg-white/5 p-6 rounded-[32px] border border-white/10 w-full md:w-auto">
                        <div v-if="atLeastOneItemSelected" class="flex items-center">
                            <input id="confirmationReceived" type="checkbox" v-model="formData.confirmationReceived" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent transition-all cursor-pointer">
                            <label for="confirmationReceived" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight cursor-pointer">Accept Items &<br>Responsibility <span class="text-red-500">*</span></label>
                        </div>
                        <div v-else class="flex items-center">
                            <input id="confirmationNotReceived" type="checkbox" v-model="formData.confirmationNotReceived" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent transition-all cursor-pointer">
                            <label for="confirmationNotReceived" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight cursor-pointer">No PPE<br>Requested <span class="text-red-500">*</span></label>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Section 4: Signature Preview -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Verified Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto text-center">
                    <label class="form-label text-slate-400 mb-6 uppercase tracking-widest">Official Record Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain animate-fadeIn" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Verified Identity</span>
                            </div>
                        </div>
                        <div v-else class="space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-black uppercase border border-red-100">
                                Signature Not Found
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">
                                Update Details
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <!-- Submission Area -->
            <div class="pt-10">
              <button 
                type="submit" 
                :disabled="!isFormValid || isSubmitting" 
                class="btn-premium disabled:grayscale disabled:opacity-50"
              >
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Processing Requisition...
                </span>
                <span v-else>Submit Completed PPE Request</span>
              </button>
               <p v-if="!isFormValid && showValidationMessage" class="mt-6 text-center text-red-600 font-bold uppercase text-xs tracking-widest animate-pulse">
                Please complete requisition requirements and sign off
               </p>
            </div>
          </form>

          <p v-if="successMessage" class="mt-10 p-8 bg-mint-50 border border-brand-accent rounded-[32px] text-center shadow-lg animate-bounce font-black text-brand-primary uppercase tracking-widest">{{ successMessage }}</p>
          <p v-if="errorMessage" class="mt-10 p-8 bg-red-50 border border-red-200 rounded-[32px] text-center font-bold text-red-700"><strong>Error:</strong> {{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  WrenchScrewdriverIcon, 
  ShieldCheckIcon, 
  PencilSquareIcon, 
  CheckBadgeIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ firstName: '', lastName: '', email: '', signatureUrl: '', signatureDataUrl: '' });
const formData = reactive({
  confirmationReceived: false,
  confirmationNotReceived: false,
});

const ppeItems = ref([
  { id: 'hiVisVest', name: 'Hi-Vis Vest', selected: false, size: 'M', qty: 1 },
  { id: 'hiVisTrousers', name: 'Hi-Vis Trousers', selected: false, size: 'M', qty: 1 },
  { id: 'safetyHelmet', name: 'Safety Helmet', selected: false, size: 'M', qty: 1 },
  { id: 'safetyFootwear', name: 'Safety Footwear', selected: false, size: '', qty: 1 },
  { id: 'gloves', name: 'Gloves', selected: false, size: 'M', qty: 1 },
  { id: 'safetyGlasses', name: 'Safety Glasses', selected: false, size: 'M', qty: 1 },
  { id: 'waterproofClothing', name: 'Waterproof Clothing', selected: false, size: 'M', qty: 1 },
  { id: 'dustMask', name: 'Dust Mask', selected: false, size: 'M', qty: 1 },
  { id: 'earDefenders', name: 'Ear Defenders', selected: false, size: 'M', qty: 1 },
]);
const otherItem = ref({ selected: false, name: '', size: '', qty: 1 });

const mainTextContent = ref('');
const isLoadingText = ref(true);
const isSubmitting = ref(false);
const showValidationMessage = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const atLeastOneItemSelected = computed(() => ppeItems.value.some(item => item.selected) || otherItem.value.selected);

watch(atLeastOneItemSelected, (newValue) => {
    if (newValue) {
        formData.confirmationNotReceived = false;
    } else {
        formData.confirmationReceived = false;
    }
});

const isFormValid = computed(() => {
  if (isLoadingText.value) return false;
  const hasSignature = !!(userProfile.signatureUrl || userProfile.signatureDataUrl);
  if (!hasSignature) return false;
  
  if (atLeastOneItemSelected.value) {
    const isItemsValid = ppeItems.value.every(item => !item.selected || item.qty > 0) && (!otherItem.value.selected || (otherItem.value.qty > 0 && otherItem.value.name));
    return isItemsValid && formData.confirmationReceived;
  }
  
  return formData.confirmationNotReceived;
});

const loadPrerequisites = async () => {
  if (!user.value) return;
  try {
    isLoadingText.value = true;
    const idTokenResult = await user.value.getIdTokenResult();
    const clientId = idTokenResult.claims.clientId;
    if (!clientId) throw new Error("Client ID missing.");

    const [profileSnap, clientSnap] = await Promise.all([
      getDoc(doc(db, 'userProfiles', user.value.uid)),
      getDoc(doc(db, 'clients', clientId))
    ]);

    if (profileSnap.exists()) {
      const p = profileSnap.data();
      userProfile.firstName = p.firstName || '';
      userProfile.lastName = p.lastName || '';
      userProfile.email = p.email || 'No email';
      userProfile.signatureUrl = p.signatureUrl || '';
      userProfile.signatureDataUrl = p.signatureDataUrl || '';
    }

    if (clientSnap.exists()) {
        mainTextContent.value = clientSnap.data().onboardingFormTexts?.ppeRequest || '';
    }
  } catch (error) {
    errorMessage.value = "Failed to load requisites.";
  } finally {
    isLoadingText.value = false;
  }
};

onMounted(loadPrerequisites);

const submitForm = async () => {
  showValidationMessage.value = true;
  if (!isFormValid.value) return;
  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  let requestedItems = {};
  if(formData.confirmationReceived){
      ppeItems.value.forEach(item => {
        if (item.selected && item.qty > 0) requestedItems[item.id] = { name: item.name, size: item.size, qty: item.qty };
      });
      if (otherItem.value.selected && otherItem.value.qty > 0 && otherItem.value.name) {
          requestedItems['other'] = { name: otherItem.value.name, size: otherItem.value.size, qty: otherItem.value.qty };
      }
  }

  const submissionData = {
    ...formData,
    items: requestedItems,
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    email: userProfile.email,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    introductoryText: mainTextContent.value,
    status: 'completed',
  };

  try {
    const submitFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFunction({
      formIdentifier: 'ppeRequest',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });

    if (result.data.success) {
      successMessage.value = `PPE Requisition submitted! Reference: ${result.data.referenceNumber}`;
      setTimeout(() => router.push({ name: 'onboardingForms' }), 2000);
    } else throw new Error(result.data.error || 'Server processing error.');
  } catch (error) {
    errorMessage.value = `Failed: ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm; 
}
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all flex justify-center items-center; 
}

.animate-fadeIn {
  animation: fadeIn 0.4s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  margin-bottom: 1.25rem;
}
:deep(.ql-editor h2) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>